import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import { useScreens } from "@hooks/useScreens";
import { Screen } from "@hooks/useScreens/types";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@utils/serverClient";
import { MdCamera } from "react-icons/md";
import ImagePreview from "@components/shared/ImagePreview";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import { useGlobalScreenshot } from "@components/screens/hooks/useGlobalScreenshot";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/ui/alert-dialog";
import { Trash2 } from "lucide-react";

interface ScreenshotDialogProps {
  screen: Screen;
  isOpen: boolean;
  onClose: () => void;
}

type Screenshot = {
  id: string;
  url?: string | null;
  created_at?: string | null;
  [k: string]: any;
};

type FetchPage = {
  data: Screenshot[];
  hasNextPage: boolean;
  total: number;
  nextPage?: number;
};

const DEFAULT_LIMIT = 12;

const fetchScreenshots = async ({
  pageParam = 0,
  screenId,
  limit = DEFAULT_LIMIT,
}: {
  pageParam?: number;
  screenId: string;
  limit?: number;
}): Promise<FetchPage> => {
  if (!screenId) {
    return { data: [], hasNextPage: false, total: 0 };
  }

  const offset = pageParam * limit;

  // Use exact count to determine pagination
  const { data, error, count } = await supabase
    .from("screenshot_assets")
    .select("*", { count: "exact" })
    .eq("screen_id", screenId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw new Error(error.message);
  }

  // count can be null if supabase didn't return it for some reason — guard against that
  const total = typeof count === "number" ? count : (data?.length ?? 0);
  const hasNextPage = total > offset + (data?.length ?? 0);

  return {
    data: data ?? [],
    hasNextPage,
    total,
    nextPage: hasNextPage ? pageParam + 1 : undefined,
  };
};

export const ScreenshotDialog: React.FC<ScreenshotDialogProps> = ({ screen, isOpen, onClose }) => {
  const { emitEvent } = useScreens();
  const { screenshotStates, updateScreenshotState } = useGlobalScreenshot();
  const queryClient = useQueryClient();
  const [totalScreenshots, setTotalScreenshots] = useState(0);

  // Get the current screenshot state for this screen
  const screenScreenshotState = screenshotStates[screen?.id || ""] || { state: "idle" };
  const isTakingScreenshot = screenScreenshotState.state === "capturing";

  // ref to the scroll container (the grid that scrolls)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Infinite query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["screenshots", screen.id],
    queryFn: ({ pageParam = 0 }) => fetchScreenshots({ pageParam, screenId: screen.id }),
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    enabled: isOpen && !!screen.id,
    initialPageParam: 0,
  });

  // Flatten screenshots
  const screenshots = data?.pages.flatMap((p) => p.data) ?? [];
  const firstPageTotal = data?.pages?.[0]?.total ?? 0;

  // Always update total (including 0)
  useEffect(() => {
    setTotalScreenshots(firstPageTotal ?? 0);
  }, [firstPageTotal]);

  // Real-time subscription: invalidate query (do not refetch which can reset pages)
  useEffect(() => {
    if (!screen.id || !isOpen) return;

    const channel = supabase
      .channel(`screenshots-${screen.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "screenshot_assets",
          filter: `screen_id=eq.${screen.id}`,
        },
        (payload) => {
          // Inserted row available as payload.new
          // Invalidate the query so new data is fetched; this won't reset stored pages.
          queryClient.invalidateQueries({ queryKey: ["screenshots", screen.id] });
          // Update the screenshot state to "complete" and then back to "idle"
          updateScreenshotState(screen.id, { state: "complete" });
          // After a short delay, set back to idle
          setTimeout(() => {
            updateScreenshotState(screen.id, { state: "idle" });
          }, 1000);
        }
      )
      .subscribe();

    return () => {
      // cleanup subscription
      supabase.removeChannel(channel);
    };
  }, [screen.id, isOpen, queryClient]);

  // Take screenshot
  const takeScreenshot = useCallback(() => {
    if (!screen.id) return;
    // Update the screen's screenshot state to "capturing"
    updateScreenshotState(screen.id, { state: "capturing" });
    emitEvent("screenshot", screen.id);
  }, [screen.id, emitEvent, updateScreenshotState]);

  // Delete screenshot
  const deleteScreenshot = useCallback(async (screenshotId: string) => {
    const { error } = await supabase
      .from("screenshot_assets")
      .delete()
      .eq("id", screenshotId);

    if (error) {
      console.error("Error deleting screenshot:", error);
      // Optionally show an error message to the user
      return;
    }

    // Invalidate the query to refresh the list
    queryClient.invalidateQueries({ queryKey: ["screenshots", screen.id] });
  }, [screen.id, queryClient]);

  // Intersection observer: use a callback ref for the last item
  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;

      // disconnect previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node || !scrollContainerRef.current) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          root: scrollContainerRef.current, // the scrolling div is the root
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      observerRef.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[85vh] w-full">
        <DialogHeader>
          <DialogTitle>Screenshots for {screen.name || "Screen"}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Take Screenshot Button at the top */}
          <div className="flex justify-between items-center">
            <Button
              variant="default"
              onClick={takeScreenshot}
              className="flex items-center gap-2"
              disabled={isTakingScreenshot}
            >
              <MdCamera className="w-4 h-4" />
              {isTakingScreenshot ? "Taking Screenshot..." : "Take Screenshot"}
            </Button>

            <p className="text-sm text-gray-500">
              {isLoading ? "Loading..." : `${totalScreenshots} screenshots`}
            </p>
          </div>

          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <CircularProgressIndicator />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* Screenshot Grid - the scrolling container */}
              <div
                ref={scrollContainerRef}
                className="grid w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto p-2"
              >
                {screenshots.map((screenshot, index) => {
                  const isLastItem = index === screenshots.length - 1;

                  return (
                    <React.Fragment key={screenshot.id}>
                      <div className="relative group">
                        {/* Dropdown Menu positioned at top right of screenshot */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full bg-background border shadow-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span className="sr-only">Open menu</span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                  />
                                </svg>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    className="cursor-pointer text-red-600 focus:bg-destructive/10 focus:text-red-600"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. This will permanently delete this screenshot.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        deleteScreenshot(screenshot.id)
                                      }}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <ImagePreview imageUrl={screenshot.url ?? ""} key={`preview-${screenshot.id}`}>
                          <div
                            ref={isLastItem ? lastItemRef : null}
                            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md bg-transparent p-4 text-black shadow-none transition-all hover:border dark:bg-transparent dark:text-gray-200"
                          >
                            <div className="w-full aspect-video flex items-center justify-center overflow-hidden rounded-md">
                              <img
                                src={screenshot.url ?? ""}
                                alt={
                                  screenshot.created_at
                                    ? `Screenshot taken at ${new Date(screenshot.created_at).toISOString()}`
                                    : "Screenshot"
                                }
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  console.error("Error loading screenshot image", e);
                                }}
                              />
                            </div>

                            {screenshot.created_at && (
                              <div className="w-full truncate text-center text-sm text-foreground">
                                {new Date(screenshot.created_at).toISOString()}
                              </div>
                            )}
                          </div>
                        </ImagePreview>
                      </div>
                    </React.Fragment>
                  );
                })}

                {isFetchingNextPage && hasNextPage && (
                  <div className="col-span-2 flex justify-center py-4">
                    <CircularProgressIndicator />
                  </div>
                )}

                {!hasNextPage && screenshots.length > 0 && (
                  <div className="mt-4 col-span-full text-center text-sm text-gray-500">
                    You've reached the end
                  </div>
                )}

                {screenshots.length === 0 && !isLoading && (
                  <div className="col-span-full flex justify-center py-8 text-gray-500">
                    No screenshots found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
